/**
 * Mirrors the predicate-building logic from predicate-store.ts getters
 * (edgePredicates, hindrancePredicates, skillPredicates, etc.).
 * If you change the filter format here, update the corresponding store getter,
 * and vice versa, until the store is refactored to delegate to this composable.
 */
import { computed, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type { FilterDescriptor } from '@progress/kendo-data-query'
import { FilterOperator } from '@progress/kendo-data-query'
import { usePredicateStore } from '@/stores/predicate-store'
import { useUxData } from '@/stores/ux-data'
import type { FilterChip } from '@/types/browser'
import type { WorldInterface } from '@/types/worlds'

// Per-type filter shapes

export interface SkillFilters {
    stringPartial: string
    linkedAttributes: string[]
}

export interface AbilityFilters {
    stringPartial: string
    types: string[]
}

export interface EdgeFilters {
    stringPartial: string
    types: string[]
}

export interface HindranceFilters {
    stringPartial: string
    severity: string[]
}

export interface ItemFilters {
    stringPartial: string
    types: string[]
}

export interface PowerFilters {
    stringPartial: string
    ranks: string[]
}

export interface FrameworkFilters {
    stringPartial: string
}

export interface AncestryFilters {
    stringPartial: string
}

export interface SettingRuleFilters {
    stringPartial: string
}

export interface RandomTableFilters {
    stringPartial: string
}

// Union of all filter shapes per type

type FilterShapeMap = {
    skills: SkillFilters
    abilities: AbilityFilters
    edges: EdgeFilters
    hindrances: HindranceFilters
    items: ItemFilters
    powers: PowerFilters
    frameworks: FrameworkFilters
    ancestries: AncestryFilters
    'setting-rules': SettingRuleFilters
    'tables': RandomTableFilters
}

export type ElementType = keyof FilterShapeMap

type InitialFilters<T extends ElementType> = Partial<FilterShapeMap[T]>

// Default filter state factories

function defaultFilters(type: 'skills'): SkillFilters
function defaultFilters(type: 'abilities'): AbilityFilters
function defaultFilters(type: 'edges'): EdgeFilters
function defaultFilters(type: 'hindrances'): HindranceFilters
function defaultFilters(type: 'items'): ItemFilters
function defaultFilters(type: 'powers'): PowerFilters
function defaultFilters(type: 'frameworks'): FrameworkFilters
function defaultFilters(type: 'ancestries'): AncestryFilters
function defaultFilters(type: 'setting-rules'): SettingRuleFilters
function defaultFilters(type: 'tables'): RandomTableFilters
// Callable union form: buildInitialState passes a generic T, which no literal overload accepts
// (an implementation signature is not callable).
function defaultFilters(type: ElementType): FilterShapeMap[ElementType]
function defaultFilters(type: ElementType): FilterShapeMap[ElementType] {
    switch (type) {
        case 'skills':
            return { stringPartial: '', linkedAttributes: [] }
        case 'abilities':
            return { stringPartial: '', types: [] }
        case 'edges':
            return { stringPartial: '', types: [] }
        case 'hindrances':
            return { stringPartial: '', severity: [] }
        case 'items':
            return { stringPartial: '', types: [] }
        case 'powers':
            return { stringPartial: '', ranks: [] }
        case 'frameworks':
            return { stringPartial: '' }
        case 'ancestries':
            return { stringPartial: '' }
        case 'setting-rules':
            return { stringPartial: '' }
        case 'tables':
            return { stringPartial: '' }
    }
}

/**
 * Per-type secondary-filter mapping (skill attribute, ability/item/edge type,
 * hindrance severity, power rank). Extracted from `useElementPredicates` so
 * surfaces that assemble their own predicate list — the world-element tab
 * (`ElementListTab`) — can reuse it without duplicating the mappings or touching
 * `predicate-store.ts`. Accepts a partial filter shape; missing fields are unset.
 */
export function buildSecondaryPredicates(
    type: ElementType,
    filters: Partial<FilterShapeMap[ElementType]>,
): FilterDescriptor[] {
    const result: FilterDescriptor[] = []
    const current = filters as FilterShapeMap[ElementType]

    switch (type) {
        case 'skills': {
            const f = current as SkillFilters
            if ((f.linkedAttributes?.length ?? 0) > 0) {
                result.push({ field: 'skill.linkedAttribute', operator: FilterOperator.EqualTo, value: f.linkedAttributes })
            }
            break
        }
        case 'abilities': {
            const f = current as AbilityFilters
            if ((f.types?.length ?? 0) > 0) {
                result.push({ field: 'ability.type', operator: FilterOperator.EqualTo, value: f.types })
            }
            break
        }
        case 'edges': {
            const f = current as EdgeFilters
            if ((f.types ?? []).includes('custom')) {
                // 'custom' is "All Other Types": NotEqualTo every known standard type not
                // also selected (returns edges whose type is blank or non-standard).
                const excludedTypes = useUxData().swadeAdvantageTypes
                    .filter((el: { label: string; value: string }) => !f.types.includes(el.value))
                    .map((el: { label: string; value: string }) => el.value)
                result.push({ field: 'edge.type', operator: FilterOperator.NotEqualTo, value: excludedTypes })
            } else if ((f.types?.length ?? 0) > 0) {
                result.push({ field: 'edge.type', operator: FilterOperator.EqualTo, value: f.types })
            }
            break
        }
        case 'hindrances': {
            const f = current as HindranceFilters
            if ((f.severity?.length ?? 0) > 0) {
                result.push({ field: 'hindrance.type', operator: FilterOperator.EqualTo, value: f.severity })
            }
            break
        }
        case 'items': {
            const f = current as ItemFilters
            if ((f.types?.length ?? 0) > 0) {
                result.push({ field: 'item.type', operator: FilterOperator.EqualTo, value: f.types })
            }
            break
        }
        case 'powers': {
            const f = current as PowerFilters
            if ((f.ranks?.length ?? 0) > 0) {
                result.push({ field: 'power.details', operator: FilterOperator.Contains, value: f.ranks })
            }
            break
        }
        // frameworks / setting-rules / tables have no secondary filter.
        default:
            break
    }

    return result
}

/**
 * Chip presentation for the same per-type secondary filters `buildSecondaryPredicates`
 * turns into query predicates. Every surface that shows filter chips over an element
 * list — the world-element tab, the `*Finder` element browsers, the guided-setup
 * picker steps — reads its chips from here so a severity/category/rank chip is worded
 * identically wherever it appears. Pair with `clearSecondaryChip` for removal.
 */
const SECONDARY_CHIP_PREFIX = 'secondary:'

export function buildSecondaryChips(
    type: ElementType,
    filters: Partial<FilterShapeMap[ElementType]>,
): FilterChip[] {
    const labels = useUxData().labels as any
    const current = filters as FilterShapeMap[ElementType]

    const toChip = (value: string, caption: string, humanized: string): FilterChip => ({
        key: `${SECONDARY_CHIP_PREFIX}${value}`,
        label: `${caption}: ${humanized}`,
    })

    switch (type) {
        case 'hindrances': {
            const f = current as HindranceFilters
            // The model carries the ['choice', <type>] sentinel pair; only the concrete
            // severity is worth a chip — 'choice' is an implementation detail.
            return (f.severity ?? [])
                .filter((value) => value !== 'choice')
                .map((value) => toChip(
                    value,
                    labels?.swade?.data?.hindrances?.category ?? 'Severity',
                    labels?.swade?.data?.hindrances?.types?.[value] ?? value,
                ))
        }
        case 'edges': {
            const f = current as EdgeFilters
            return (f.types ?? []).map((value) => toChip(
                value,
                labels?.swade?.data?.edges?.category ?? 'Category',
                labels?.swade?.data?.edges?.types?.[value] ?? value,
            ))
        }
        case 'skills': {
            const f = current as SkillFilters
            return (f.linkedAttributes ?? []).map((value) => toChip(
                value,
                labels?.swade?.data?.skills?.linkedAttribute ?? 'Attribute',
                labels?.swade?.attributes?.[value] ?? value,
            ))
        }
        case 'items': {
            const f = current as ItemFilters
            return (f.types ?? []).map((value) => toChip(
                value,
                labels?.swade?.data?.equipment?.type ?? 'Type',
                labels?.swade?.data?.equipment?.types?.[value] ?? value,
            ))
        }
        case 'powers': {
            const f = current as PowerFilters
            return (f.ranks ?? []).map((value) => toChip(
                value,
                labels?.swade?.data?.powers?.rank ?? 'Rank',
                labels?.swade?.rank?.[value] ?? value,
            ))
        }
        case 'abilities': {
            const f = current as AbilityFilters
            return (f.types ?? []).map((value) => toChip(
                value,
                labels?.swade?.data?.abilities?.type ?? 'Type',
                labels?.swade?.data?.abilities?.types?.[value] ?? value,
            ))
        }
        // frameworks / setting-rules / tables have no secondary filter.
        default:
            return []
    }
}

/** Drop the value a chip stands for from `filters`, mutating it in place. */
export function clearSecondaryChip(
    type: ElementType,
    filters: Partial<FilterShapeMap[ElementType]>,
    chip: FilterChip,
): void {
    const value = chip.key.replace(SECONDARY_CHIP_PREFIX, '')
    const current = filters as any

    switch (type) {
        case 'hindrances':
            // Clearing the concrete severity has to take the 'choice' sentinel with it.
            current.severity = []
            break
        case 'skills':
            current.linkedAttributes = (current.linkedAttributes ?? []).filter((item: string) => item !== value)
            break
        case 'powers':
            current.ranks = (current.ranks ?? []).filter((item: string) => item !== value)
            break
        case 'edges':
        case 'items':
        case 'abilities':
            current.types = (current.types ?? []).filter((item: string) => item !== value)
            break
        default:
            break
    }
}

export interface ElementPredicateOptions {
    /**
     * Override the `world.uuid` predicate instead of reading `predicateStore.worlds`.
     * `null` (the default) means "defer to the shared slot". The `*Finder` browsers pass
     * `useCharacterWorldScope()` so they scope to the character being edited rather than to
     * whatever element scope another mounted component last wrote to that slot.
     */
    worlds?: Ref<WorldInterface[] | null> | ComputedRef<WorldInterface[] | null>
}

export function useElementPredicates<T extends ElementType>(
    type: T,
    initialFilters?: InitialFilters<T>,
    options?: ElementPredicateOptions,
): {
    filters: Ref<FilterShapeMap[T]>
    predicates: ComputedRef<FilterDescriptor[]>
    resetFilters: () => void
} {
    const predicateStore = usePredicateStore()

    // Merge defaults with any initial overrides
    const buildInitialState = (): FilterShapeMap[T] => {
        const base = defaultFilters(type as ElementType) as FilterShapeMap[T]
        if (initialFilters) {
            return { ...base, ...initialFilters }
        }
        return base
    }

    const filters = ref(buildInitialState()) as Ref<FilterShapeMap[T]>

    const predicates = computed<FilterDescriptor[]>(() => {
        const result: FilterDescriptor[] = []

        // Context: system
        if (predicateStore.system !== null) {
            result.push({
                field: 'system.uuid',
                operator: FilterOperator.EqualTo,
                value: predicateStore.system.uuid,
            })
        }

        // Context: worlds. An explicit scope wins outright — it replaces the shared slot
        // rather than adding to it, so a stale element scope can't survive as a second
        // (contradictory) world.uuid predicate.
        const scopedWorlds = options?.worlds?.value ?? null
        const worlds = scopedWorlds ?? predicateStore.worlds
        if (worlds.length > 0) {
            result.push({
                field: 'world.uuid',
                operator: FilterOperator.EqualTo,
                value: worlds.map((w) => w.uuid),
            })
        }

        // Context: sources
        if (predicateStore.sources.length > 0) {
            result.push({
                field: 'source.uuid',
                operator: FilterOperator.EqualTo,
                value: predicateStore.sources.map((s) => s.uuid),
            })
        }

        // Local: stringPartial
        const current = filters.value as FilterShapeMap[ElementType]
        if (current.stringPartial.length > 0) {
            const nameField = type === 'setting-rules' ? 'settingRule.name' : 'element.name'
            result.push({
                field: nameField,
                operator: FilterOperator.Contains,
                value: current.stringPartial,
            })
        }

        // Per-type secondary filters (extracted + shared with ElementListTab).
        result.push(...buildSecondaryPredicates(type, current))

        // Context: trashed
        if (predicateStore.trashed) {
            result.push({
                field: 'element.dateDeleted',
                operator: FilterOperator.IsNotNull,
                value: predicateStore.trashed,
            })
            result.push({
                field: 'owner.uuid',
                operator: FilterOperator.EqualTo,
                value: ':user',
            })
        }

        return result
    })

    const resetFilters = (): void => {
        filters.value = buildInitialState()
    }

    return { filters, predicates, resetFilters }
}
